import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import ExclamationIcon from '@tecuity/sos-icons/react/exclamation-point'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { Accordion } from '@tecuity/granite-ui/Accordion'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { SET_CORRESPONDENCE2_REDUCER } from '../Correspondence2Reducer'
import { GET_APP_SETTING_BY_NAME } from '../../../requests'

const ReasonsStep = () => {
  const dispatch = useDispatch()
  const corrInfo = useSelector((state) => state.correspondence2, shallowEqual)
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const { CustomCorrespondenceReasons, generalReasons, specificReasons, EntryTypeId, readOnly } =
    corrInfo

  useEffect(() => {
    GET_APP_SETTING_BY_NAME(
      'CorrIncludeOriginalImageDefaultValue',
      (data) => {
        dispatch({
          type: SET_CORRESPONDENCE2_REDUCER,
          payload: {
            IncludeOriginalImage : true,
          },
        })
      },
      (err) => {
        console.error(err)
      }
    )
  }, [])

  const areParamsFilled = (reasonType) => {
    const checkedReasonsByType = CustomCorrespondenceReasons.filter(
      (item) => item.paramsList && item.paramsList.length > 0 && item.parent === reasonType
    )
    return checkedReasonsByType.every((reason) =>
      reason.paramsList.every((param) => param.value !== '')
    )
  }

  const toggleAccordion = (category) => {
    if (activeTabId === 'tab-1') {
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          generalReasons: generalReasons.map((cg) => {
            if (cg.Category === category) cg.isOpen = !cg.isOpen
            return cg
          }),
        },
      })
    } else {
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          specificReasons: specificReasons.map((cg) => {
            if (cg.Category === category) cg.isOpen = !cg.isOpen
            return cg
          }),
        },
      })
    }
  }

  const areCategoryParamsFilled = (categoryObject) => {
    return categoryObject.Reasons.every(
      (reason) =>
        !reason.checked ||
        !reason.paramsList ||
        reason.paramsList.every((param) => param.value !== '')
    )
  }

  const checkedChange = (reasons, category, reason) => {
    return reasons.map((cg) => {
      if (cg.Category === category.Category) {
        cg.Reasons = cg.Reasons.map((rsn) => {
          if (rsn.ID === reason.ID) rsn.checked = rsn.checked ? undefined : true
          return rsn
        })
      }
      return cg
    })
  }

  const handleCheckedChange = (category, reason) => {
    dispatch({
      type: SET_CORRESPONDENCE2_REDUCER,
      payload: {
        [activeTabId === 'tab-1' ? 'generalReasons' : 'specificReasons']: checkedChange(
          activeTabId === 'tab-1' ? generalReasons : specificReasons,
          category,
          reason
        ),
      },
    })
  }

  const paramChange = (reasons, category, reason) => {
    return reasons.map((cg) => {
      if (cg.Category === category.Category) {
        cg.Reasons = cg.Reasons.map((rsn) => {
          if (rsn.ID === reason.ID) return reason
          return rsn
        })
      }
      return cg
    })
  }

  const handleParamChange = (category, reason) => {
    dispatch({
      type: SET_CORRESPONDENCE2_REDUCER,
      payload: {
        [activeTabId === 'tab-1' ? 'generalReasons' : 'specificReasons']: paramChange(
          activeTabId === 'tab-1' ? generalReasons : specificReasons,
          category,
          reason
        ),
      },
    })
  }

  return (
    <div className="corr-field-container">
      {EntryTypeId === 1119 /* Paper Filed */ && (
        <Checkbox
          label="Include Original Image"
          aria-label="Include Original Image"
          checked={corrInfo.IncludeOriginalImage || false}
          onClick={(e) =>
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { IncludeOriginalImage: e.target.checked },
            })
          }
          disabled={readOnly ? true : undefined}
        />
      )}
      <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
        <div className="tablist" role="tablist">
          <TabButton
            className={!areParamsFilled('GeneralReasons') && 'error'}
            id="tab-1"
            forPanelId="panel-1"
            label="General Reasons"
            rightIcon={
              !areParamsFilled('GeneralReasons') ? (
                <div className="triangle">
                  <ExclamationIcon />
                </div>
              ) : null
            }
          />
          <TabButton
            className={!areParamsFilled('SpecificReasons') && 'error'}
            id="tab-2"
            forPanelId="panel-2"
            label="Specific Reasons"
            rightIcon={
              !areParamsFilled('SpecificReasons') ? (
                <div className="triangle">
                  <ExclamationIcon />
                </div>
              ) : null
            }
          />
        </div>
        <TabPanel id="panel-1" tabId="tab-1">
          <br />
          {generalReasons.map((categoryObject, index) => {
            return (
              <Accordion
                className="accordion"
                key={'gen-' + index}
                isOpen={categoryObject.isOpen}
                onChange={() => toggleAccordion(categoryObject.Category)}
                title={[
                  <span key={index} style={{ justifyContent: 'flex-start' }}>
                    {categoryObject.Category}
                  </span>,
                  !areCategoryParamsFilled(categoryObject) ? (
                    <div className="outer-triangle">
                      <div className="triangle">
                        <ExclamationIcon />
                      </div>
                    </div>
                  ) : (
                    ''
                  ),
                ]}
              >
                {categoryObject.Reasons.map((reasonObj) => {
                  return (
                    <ReasonParser
                      key={reasonObj.ID}
                      categoryObject={categoryObject}
                      reasonObj={reasonObj}
                      handleCheckedChange={handleCheckedChange}
                      handleParamChange={handleParamChange}
                    />
                  )
                })}
              </Accordion>
            )
          })}
        </TabPanel>
        <TabPanel id="panel-2" tabId="tab-2">
          <br />
          {specificReasons.map((categoryObject, index) => {
            return (
              <Accordion
                className="accordion"
                key={'spec-' + index}
                isOpen={categoryObject.isOpen}
                onChange={() => toggleAccordion(categoryObject.Category)}
                title={[
                  <span key={index} style={{ justifyContent: 'flex-start' }}>
                    {categoryObject.Category}
                  </span>,
                  !areCategoryParamsFilled(categoryObject) ? (
                    <div className="outer-triangle">
                      <div className="triangle">
                        <ExclamationIcon />
                      </div>
                    </div>
                  ) : (
                    ''
                  ),
                ]}
              >
                {categoryObject.Reasons.map((reasonObj) => {
                  return (
                    <ReasonParser
                      key={reasonObj.ID}
                      categoryObject={categoryObject}
                      reasonObj={reasonObj}
                      handleCheckedChange={handleCheckedChange}
                      handleParamChange={handleParamChange}
                    />
                  )
                })}
              </Accordion>
            )
          })}
        </TabPanel>
      </TabProvider>
    </div>
  )
}

export default ReasonsStep

export const isReasonStepValid = (corrInfo) => {
  const { CustomCorrespondenceReasons } = corrInfo
  if (!CustomCorrespondenceReasons || CustomCorrespondenceReasons.length === 0) return false
  return CustomCorrespondenceReasons.every((reason) => {
    if (!reason.ParamList || reason.ParamList.length === 0) return true
    if (typeof reason.ParamList === 'object') {
      return true
    }
    return reason.ParamList.every((param) => param.value.trim() !== '')
  })
}

const ReasonParser = ({ categoryObject, reasonObj, handleCheckedChange, handleParamChange }) => {
  const [parsedObj, setParsedObj] = useState(reasonObj)
  const { showParamsError, readOnly } = useSelector((state) => state.correspondence2, shallowEqual)

  useEffect(() => {
    if (!reasonObj.checked) {
      setParsedObj(reasonObj)
    } else {
      let reasonSplit = reasonObj.ShortReason.split('[')
      let modifiedObject = { ...reasonObj }

      if (!modifiedObject.paramsList) {
        modifiedObject.paramsList = []

        reasonSplit.forEach((part, index) => {
          if (index === 0) return
          let subSplit = part.split(']')
          modifiedObject.paramsList.push({ key: `[${subSplit[0]}]`, value: '' })
        })
      }

      setParsedObj(modifiedObject)
    }
  }, [reasonObj.checked])

  useEffect(() => {
    if (parsedObj.paramsList && parsedObj.paramsList.length > 0) {
      let areParamsFilled = true
      parsedObj.paramsList.forEach((param) => {
        if (!param.value || param.value.trim() === '') areParamsFilled = false
      })
      setParsedObj({ ...parsedObj, areParamsFilled })
    }
  }, [parsedObj.paramsList, parsedObj.checked])

  useEffect(() => {
    if (parsedObj.checked && parsedObj.paramsList && parsedObj.paramsList.length > 0) {
      handleParamChange(categoryObject, { ...parsedObj, parsedReason: parsedReason })
    }
  }, [parsedObj, parsedReason])

  const parsedReason = useMemo(() => {
    if (!parsedObj.paramsList || parsedObj.paramsList.length === 0) return undefined

    let tempReason = parsedObj.LongReason
    parsedObj.paramsList.forEach((param) => {
      tempReason = tempReason.replace(
        readOnly ? `[${param.key}]` : param.key,
        param.value || param.key
      )
    })
    return tempReason
  }, [parsedObj, readOnly])

  const setParamValue = (value, index) => {
    let changedList = [...parsedObj.paramsList]
    changedList[index] = { ...changedList[index], value: value }

    setParsedObj({
      ...parsedObj,
      paramsList: changedList,
    })
  }

  const labelWithInputs = useMemo(() => {
    if (parsedObj.paramsList && parsedObj.checked) {
      const { paramsList } = parsedObj
      const reasonSplit = parsedObj.ShortReason.split('[')

      if (reasonSplit.length === 1 && paramsList.length === 1) {
        const subSplit = reasonSplit[0].split(']')
        return (
          <span>
            <input
              type="text"
              className={`reason-input ${
                showParamsError && !paramsList[0].value ? `error-param` : ``
              }`}
              value={paramsList[0].value || ''}
              onChange={(e) => {
                setParamValue(e.target.value, 0)
              }}
              disabled={readOnly ? true : undefined}
              style={{
                width: paramsList[0].key.includes('SHORT')
                  ? '75px'
                  : paramsList[0].key.includes('LONG')
                  ? '95%'
                  : '190px',
              }}
            />
            {subSplit.length > 1 ? subSplit[1] : null}
          </span>
        )
      }
      return (
        <span>
          {reasonSplit.map((part, index) => {
            if (index === 0) {
              return part
            } else {
              const subPart = part.split(']')
              return (
                <React.Fragment key={paramsList[index - 1].key}>
                  <input
                    type="text"
                    className={`reason-input ${
                      showParamsError && !paramsList[index - 1].value ? `error-param` : ``
                    }`}
                    value={paramsList[index - 1].value || ''}
                    onChange={(e) => {
                      setParamValue(e.target.value, index - 1)
                    }}
                    disabled={readOnly ? true : undefined}
                    style={{
                      width: paramsList[index - 1].key.includes('SHORT')
                        ? '75px'
                        : paramsList[index - 1].key.includes('LONG')
                        ? '95%'
                        : '190px',
                    }}
                  />
                  {subPart.length > 1 ? subPart[1] : null}
                </React.Fragment>
              )
            }
          })}
        </span>
      )
    }
    return undefined
  }, [parsedObj, parsedReason])

  return (
    <Checkbox
      disabled={readOnly ? true : undefined}
      objectLabel={labelWithInputs}
      label={parsedObj.ShortReason}
      checked={!!parsedObj.checked}
      onChange={() => handleCheckedChange(categoryObject, parsedObj)}
    />
  )
}
